/**
 * QuestionnaireItemRow - Single item with inline editing, debug info,
 * source management, and regenerate functionality
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { QuestionnaireItem, UpdateItemRequest, BatchDebugInfo, CustomField, ApproveItemRequest } from '../../types';
import { refreshSources } from '../../services/api';
import { approveQuestionnaireItem } from '../../services/questionnaireService';
import { formatCompleteResponse, copyToClipboard } from '../../utils/formatCompleteResponse';
import './QuestionnaireItemRow.css';

export interface URLMapping {
  filename: string;
  public_url: string;
}

interface QuestionnaireItemRowProps {
  item: QuestionnaireItem;
  projectId: string;
  customFields: CustomField[];
  urlMappings: URLMapping[];  // Passed from parent to avoid N+1 API calls
  onSave: (itemId: string, updates: UpdateItemRequest) => Promise<void>;
  onReprocess: (itemId: string, additionalContext?: string) => Promise<void>;
  onUpdateSources: (itemId: string, evidence: string[]) => Promise<void>;
  onAddToQA?: (item: QuestionnaireItem, customerName: string) => Promise<void>;
  onApprovalChange?: (itemId: string, isApproved: boolean) => void;  // Callback to update parent state
  isSaving: boolean;
  isAddedToQA?: boolean;
  currentUserEmail?: string;      // For tracking who approved
  currentUserName?: string;       // For tracking who approved
}

const DEBOUNCE_MS = 1500;

const QuestionnaireItemRow: React.FC<QuestionnaireItemRowProps> = ({
  item,
  projectId,
  customFields,
  urlMappings,
  onSave,
  onReprocess,
  onUpdateSources,
  onAddToQA,
  onApprovalChange,
  isSaving,
  isAddedToQA = false,
  currentUserEmail,
  currentUserName,
}) => {
  const [response, setResponse] = useState(item.response || '');
  const [customFieldValues, setCustomFieldValues] = useState<Record<string, any>>(item.custom_field_values || {});
  // Two-stage expansion: 'collapsed' -> 'preview' -> 'edit'
  const [expansionState, setExpansionState] = useState<'collapsed' | 'preview' | 'edit'>('collapsed');
  const [showDebug, setShowDebug] = useState(false);

  // Approval status from DB: true=approved, false=needs edit, uncertain, null/undefined=not reviewed
  // Derive local state from item prop
  const getApprovalStatusFromItem = (): 'approved' | 'needs_edit' | 'uncertain' | null => {
    if (item.is_uncertain) return 'uncertain';
    if (item.is_approved === true) return 'approved';
    if (item.is_approved === false) return 'needs_edit';
    return null;
  };
  const [approvalStatus, setApprovalStatus] = useState<'approved' | 'needs_edit' | 'uncertain' | null>(getApprovalStatusFromItem());
  const [isApprovingSaving, setIsApprovingSaving] = useState(false);
  // Track approver info locally so it updates immediately after approval
  const [localApproverName, setLocalApproverName] = useState<string | null>(item.approved_by_name || item.approved_by || null);
  const [localApprovedAt, setLocalApprovedAt] = useState<string | null>(item.approved_at || null);
  // Track editor info locally so it updates immediately during editing
  const [localEditorName, setLocalEditorName] = useState<string | null>(item.last_edited_by_name || item.last_edited_by || null);
  const [isLocallyEdited, setIsLocallyEdited] = useState(item.is_manually_edited || false);

  // Sync approval status when item changes (e.g., after refetch)
  useEffect(() => {
    setApprovalStatus(getApprovalStatusFromItem());
    setLocalApproverName(item.approved_by_name || item.approved_by || null);
    setLocalApprovedAt(item.approved_at || null);
    setLocalEditorName(item.last_edited_by_name || item.last_edited_by || null);
    setIsLocallyEdited(item.is_manually_edited || false);
  }, [item.is_approved, item.is_uncertain, item.approved_by_name, item.approved_by, item.approved_at, item.last_edited_by_name, item.last_edited_by, item.is_manually_edited]);

  // Helper derived states for backward compatibility
  const isExpanded = expansionState === 'edit';
  const isPreviewExpanded = expansionState === 'preview';
  const [hasChanges, setHasChanges] = useState(false);
  const [copied, setCopied] = useState(false);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const customFieldDebounceRef = useRef<NodeJS.Timeout | null>(null);

  // Regenerate modal state
  const [showRegenerateModal, setShowRegenerateModal] = useState(false);
  const [additionalContext, setAdditionalContext] = useState('');
  const [isRegenerating, setIsRegenerating] = useState(false);

  // Add to Q&A modal state
  const [showAddToQAModal, setShowAddToQAModal] = useState(false);
  const [customerNameInput, setCustomerNameInput] = useState('');
  const [isAddingToQA, setIsAddingToQA] = useState(false);

  // Source management state
  const [isRefreshingSources, setIsRefreshingSources] = useState(false);
  const [showAddSourcePanel, setShowAddSourcePanel] = useState(false);
  const [sourceSearchTerm, setSourceSearchTerm] = useState('');

  // Sync with prop changes (e.g., after reprocessing)
  useEffect(() => {
    setResponse(item.response || '');
    setCustomFieldValues(item.custom_field_values || {});
    setHasChanges(false);
  }, [item.response, item.custom_field_values]);

  const handleResponseChange = useCallback((value: string) => {
    setResponse(value);
    setHasChanges(true);

    // Mark as locally edited immediately so the "Edited by" badge shows
    setIsLocallyEdited(true);
    setLocalEditorName(currentUserName || currentUserEmail || 'You');

    // If item was approved, reset approval status since it's being edited
    if (approvalStatus === 'approved') {
      // Reset locally immediately
      setApprovalStatus(null);
      setLocalApproverName(null);
      setLocalApprovedAt(null);
      // Notify parent and persist the reset
      onApprovalChange?.(item.id, false); // This will trigger a re-fetch or state update
      // Reset approval in backend
      approveQuestionnaireItem(item.id, {
        is_approved: null as any, // Reset to null (not reviewed)
        approved_by: currentUserEmail,
        approved_by_name: currentUserName,
      }).catch(err => console.error('Failed to reset approval:', err));
    }

    // If item was uncertain, transition to edited state
    if (approvalStatus === 'uncertain') {
      // Reset locally immediately - now it's "edited" not "uncertain"
      setApprovalStatus(null);
      // Notify parent
      onApprovalChange?.(item.id, false);
      // Clear uncertain flag in backend (sets is_uncertain=false, keeps is_approved=null)
      approveQuestionnaireItem(item.id, {
        is_approved: null as any, // Keep as not reviewed
        is_uncertain: false, // Clear the uncertain flag
        approved_by: currentUserEmail,
        approved_by_name: currentUserName,
      }).catch(err => console.error('Failed to clear uncertain status:', err));
    }

    // Debounced auto-save
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
    }
    debounceRef.current = setTimeout(() => {
      onSave(item.id, { response: value });
      setHasChanges(false);
    }, DEBOUNCE_MS);
  }, [item.id, onSave, approvalStatus, currentUserEmail, currentUserName, onApprovalChange]);

  const handleCustomFieldChange = useCallback((fieldName: string, value: string) => {
    setCustomFieldValues(prev => ({ ...prev, [fieldName]: value }));
    setHasChanges(true);

    // Mark as locally edited
    setIsLocallyEdited(true);
    setLocalEditorName(currentUserName || currentUserEmail || 'You');

    // If item was uncertain, transition to edited state
    if (approvalStatus === 'uncertain') {
      setApprovalStatus(null);
      onApprovalChange?.(item.id, false);
      approveQuestionnaireItem(item.id, {
        is_approved: null as any,
        is_uncertain: false,
        approved_by: currentUserEmail,
        approved_by_name: currentUserName,
      }).catch(err => console.error('Failed to clear uncertain status:', err));
    }

    // Debounced auto-save for custom fields
    if (customFieldDebounceRef.current) {
      clearTimeout(customFieldDebounceRef.current);
    }
    customFieldDebounceRef.current = setTimeout(() => {
      onSave(item.id, { custom_field_values: { ...customFieldValues, [fieldName]: value } });
      setHasChanges(false);
    }, DEBOUNCE_MS);
  }, [item.id, onSave, customFieldValues, approvalStatus, currentUserEmail, currentUserName, onApprovalChange]);

  // Copy complete response to clipboard
  const handleCopyResponse = async () => {
    // Convert evidence URLs to sources format for formatting
    const sources = (item.evidence || []).map(url => ({
      doc_name: getSourceDisplayName(url),
      url
    }));
    const text = formatCompleteResponse(response, sources);
    const success = await copyToClipboard(text);
    if (success) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
      if (customFieldDebounceRef.current) {
        clearTimeout(customFieldDebounceRef.current);
      }
    };
  }, []);

  // Check if this is a "new question type" (no good Q&A match >= 60%)
  const isNewQuestionType = item.debug_info &&
    item.debug_info.qa_matches &&
    item.debug_info.qa_matches.length > 0 &&
    !item.debug_info.qa_match_used &&
    item.debug_info.qa_matches[0].similarity_score < 0.6;

  const getConfidenceColor = () => {
    if (!item.confidence) return '#6b7280';
    if (item.confidence >= 0.8) return '#10b981';
    if (item.confidence >= 0.5) return '#f59e0b';
    return '#ef4444';
  };

  // === Source Management Functions ===

  const handleRefreshSources = async () => {
    if (isRefreshingSources || !item.response) return;

    setIsRefreshingSources(true);
    try {
      const result = await refreshSources(projectId, item.response, item.original_query);
      if (result.sources && result.sources.length > 0) {
        const existingUrls = new Set(item.evidence || []);
        const newUrls = result.sources
          .map(s => s.url)
          .filter((url): url is string => !!url && !existingUrls.has(url));

        if (newUrls.length > 0) {
          const mergedEvidence = [...(item.evidence || []), ...newUrls];
          await onUpdateSources(item.id, mergedEvidence);
        }
      }
    } catch (error) {
      console.error('Failed to refresh sources:', error);
    } finally {
      setIsRefreshingSources(false);
    }
  };

  const handleRemoveSource = async (urlToRemove: string) => {
    const updatedEvidence = (item.evidence || []).filter(url => url !== urlToRemove);
    await onUpdateSources(item.id, updatedEvidence);
  };

  const handleOpenAddSource = () => {
    setShowAddSourcePanel(true);
    setSourceSearchTerm('');
  };

  const handleAddSource = async (source: { filename: string; public_url: string }) => {
    if (!source.public_url) return;
    if ((item.evidence || []).includes(source.public_url)) return; // Already exists

    const updatedEvidence = [...(item.evidence || []), source.public_url];
    await onUpdateSources(item.id, updatedEvidence);
  };

  const getFilteredSources = () => {
    const existingUrls = new Set(item.evidence || []);
    return urlMappings
      .filter(s => !existingUrls.has(s.public_url))
      .filter(s => s.filename.toLowerCase().includes(sourceSearchTerm.toLowerCase()));
  };

  // === Regenerate Functions ===

  const handleOpenRegenerateModal = () => {
    setAdditionalContext('');
    setShowRegenerateModal(true);
  };

  const handleConfirmRegenerate = async () => {
    if (!additionalContext.trim()) return;

    setIsRegenerating(true);
    try {
      await onReprocess(item.id, additionalContext.trim());
      setShowRegenerateModal(false);
      setAdditionalContext('');
      // Mark as edited since user provided custom context for regeneration
      setIsLocallyEdited(true);
      setLocalEditorName(currentUserName || currentUserEmail || 'You');
    } finally {
      setIsRegenerating(false);
    }
  };

  const handleCancelRegenerate = () => {
    setShowRegenerateModal(false);
    setAdditionalContext('');
  };

  // === Add to Q&A Functions ===

  const handleOpenAddToQAModal = () => {
    setCustomerNameInput('');
    setShowAddToQAModal(true);
  };

  const handleConfirmAddToQA = async () => {
    if (!customerNameInput.trim() || !onAddToQA) return;

    setIsAddingToQA(true);
    try {
      await onAddToQA(item, customerNameInput.trim());
      setShowAddToQAModal(false);
      setCustomerNameInput('');
    } catch (error) {
      console.error('Failed to add to Q&A:', error);
    } finally {
      setIsAddingToQA(false);
    }
  };

  const handleCancelAddToQA = () => {
    setShowAddToQAModal(false);
    setCustomerNameInput('');
  };

  // Get display name for a URL - first check URL mappings, then extract from URL path
  const getSourceDisplayName = (url: string): string => {
    // First, try to find the filename from URL mappings
    const mapping = urlMappings.find(m => m.public_url === url);
    if (mapping) {
      return mapping.filename;
    }

    // Fallback: extract filename from URL path
    try {
      const urlObj = new URL(url);
      const pathname = urlObj.pathname;
      const filename = pathname.split('/').pop();
      if (filename && filename.length > 0 && filename !== 'view') {
        return decodeURIComponent(filename);
      }
      // If no good filename, show truncated URL
      return url.length > 50 ? url.substring(0, 47) + '...' : url;
    } catch {
      return url;
    }
  };

  const renderDebugPanel = (debugInfo: BatchDebugInfo) => {
    return (
      <div className="debug-panel">
        <div className="debug-header">
          <span className="debug-title">🔍 Debug Info</span>
          <span className={`debug-badge ${debugInfo.qa_match_used ? 'qa' : 'rag'}`}>
            {debugInfo.qa_match_used ? '📋 Q&A Match Used' : '📄 RAG Generated'}
          </span>
        </div>

        {/* Q&A Matches Section */}
        {debugInfo.qa_matches && debugInfo.qa_matches.length > 0 && (
          <div className="debug-section">
            <h4>📋 Q&A Matches (Top {debugInfo.qa_matches.length})</h4>
            <div className="debug-items">
              {debugInfo.qa_matches.map((qa, idx) => (
                <div key={idx} className={`debug-item ${idx === 0 && debugInfo.qa_match_used ? 'used' : ''}`}>
                  <div className="debug-item-header">
                    <span className={`similarity-badge ${qa.similarity_score >= 0.85 ? 'high' : 'medium'}`}>
                      {Math.round(qa.similarity_score * 100)}%
                    </span>
                    <span className="debug-meta">
                      {qa.client_name} • {qa.response_date}
                    </span>
                    {idx === 0 && debugInfo.qa_match_used && (
                      <span className="used-badge">✓ Used</span>
                    )}
                  </div>
                  <div className="debug-item-preview">{qa.matched_question}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Policy Chunks Section */}
        {debugInfo.policy_chunks && debugInfo.policy_chunks.length > 0 && (
          <div className="debug-section">
            <h4>📄 Policy Document Chunks</h4>
            <div className="debug-items">
              {debugInfo.policy_chunks.map((chunk, idx) => (
                <div key={idx} className="debug-item">
                  <div className="debug-item-header">
                    {chunk.url ? (
                      <a href={chunk.url} target="_blank" rel="noopener noreferrer">
                        📄 {chunk.doc_name}
                      </a>
                    ) : (
                      <span>📄 {chunk.doc_name}</span>
                    )}
                    <span
                      className={`similarity-badge ${chunk.similarity_score >= 0.7 ? 'high' : 'medium'}`}
                    >
                      {Math.round(chunk.similarity_score * 100)}%
                    </span>
                  </div>
                  <div className="debug-item-preview">{chunk.text_preview}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LLM Prompt Section */}
        {debugInfo.llm_prompt && (
          <div className="debug-section">
            <h4>🤖 LLM Prompt Sent to Gemini</h4>
            <div className="debug-prompt-container">
              <button
                className="copy-prompt-btn"
                onClick={() => {
                  navigator.clipboard.writeText(debugInfo.llm_prompt || '');
                  alert('Prompt copied to clipboard!');
                }}
              >
                📋 Copy Full Prompt
              </button>
              <pre className="debug-prompt">{debugInfo.llm_prompt}</pre>
            </div>
          </div>
        )}

        {/* Edit History Section */}
        {item.edit_history && item.edit_history.length > 0 && (
          <div className="debug-section">
            <h4>✏️ Edit History ({item.edit_history.length})</h4>
            <div className="debug-items">
              {item.edit_history.slice().reverse().map((edit, idx) => (
                <div key={idx} className="debug-item">
                  <div className="debug-item-header">
                    <span className="edit-author">
                      👤 {edit.edited_by_name || edit.edited_by}
                    </span>
                    <span className="debug-meta">
                      {edit.edited_at ? new Date(edit.edited_at).toLocaleString() : 'Unknown time'}
                    </span>
                  </div>
                  {edit.changes && edit.changes.length > 0 && (
                    <div className="edit-changes">
                      Changed: {edit.changes.join(', ')}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  // Truncate response for preview (collapsed view)
  const getResponsePreview = (maxLength: number = 100) => {
    const text = response || '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Truncate question for preview (collapsed view)
  const getQuestionPreview = (maxLength: number = 120) => {
    const text = item.original_query || '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  };

  // Get source names for preview (truncated)
  const getSourcesPreview = (maxSources: number = 2) => {
    if (!item.evidence || item.evidence.length === 0) return null;
    const sourceNames = item.evidence.slice(0, maxSources).map(url => getSourceDisplayName(url));
    const remaining = item.evidence.length - maxSources;
    if (remaining > 0) {
      return `${sourceNames.join(', ')} +${remaining} more`;
    }
    return sourceNames.join(', ');
  };

  // Handle row click - cycle through collapsed → preview → edit → collapsed
  const handleRowClick = () => {
    if (expansionState === 'collapsed') {
      setExpansionState('preview');
    } else if (expansionState === 'preview') {
      setExpansionState('edit');
    } else {
      setExpansionState('collapsed');
    }
  };

  // Enter edit mode explicitly
  const handleEnterEditMode = (e: React.MouseEvent) => {
    e.stopPropagation();
    setExpansionState('edit');
  };

  // Close edit mode
  const handleCloseEditMode = () => {
    setExpansionState('collapsed');
  };

  // Handle approve (green tick) - calls API to persist
  const handleApprove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isApprovingSaving) return;

    setIsApprovingSaving(true);
    try {
      await approveQuestionnaireItem(item.id, {
        is_approved: true,
        approved_by: currentUserEmail,
        approved_by_name: currentUserName,
      });
      setApprovalStatus('approved');
      // Update local approver info immediately
      setLocalApproverName(currentUserName || currentUserEmail || 'You');
      setLocalApprovedAt(new Date().toISOString());
      onApprovalChange?.(item.id, true);
    } catch (error) {
      console.error('Failed to approve item:', error);
    } finally {
      setIsApprovingSaving(false);
    }
  };

  // Handle reject (red cross) - calls API to persist, opens edit mode
  const handleReject = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isApprovingSaving) return;

    setIsApprovingSaving(true);
    try {
      await approveQuestionnaireItem(item.id, {
        is_approved: false,
        approved_by: currentUserEmail,
        approved_by_name: currentUserName,
      });
      setApprovalStatus('needs_edit');
      // Update local approver info immediately
      setLocalApproverName(currentUserName || currentUserEmail || 'You');
      setLocalApprovedAt(new Date().toISOString());
      setExpansionState('edit');
      onApprovalChange?.(item.id, false);
    } catch (error) {
      console.error('Failed to reject item:', error);
    } finally {
      setIsApprovingSaving(false);
    }
  };

  // Handle uncertain (?) - calls API to persist, marks as needs further review
  const handleUncertain = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isApprovingSaving) return;

    setIsApprovingSaving(true);
    try {
      await approveQuestionnaireItem(item.id, {
        is_uncertain: true,
        approved_by: currentUserEmail,
        approved_by_name: currentUserName,
      });
      setApprovalStatus('uncertain');
      // Update local approver info immediately
      setLocalApproverName(currentUserName || currentUserEmail || 'You');
      setLocalApprovedAt(new Date().toISOString());
      onApprovalChange?.(item.id, false);
    } catch (error) {
      console.error('Failed to mark item as uncertain:', error);
    } finally {
      setIsApprovingSaving(false);
    }
  };

  // Handle "Done Editing" - when user finishes editing a needs_edit item, mark as approved
  const handleDoneEditing = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isApprovingSaving) return;

    setIsApprovingSaving(true);
    try {
      await approveQuestionnaireItem(item.id, {
        is_approved: true,
        approved_by: currentUserEmail,
        approved_by_name: currentUserName,
      });
      setApprovalStatus('approved');
      setLocalApproverName(currentUserName || currentUserEmail || 'You');
      setLocalApprovedAt(new Date().toISOString());
      onApprovalChange?.(item.id, true);
    } catch (error) {
      console.error('Failed to mark as done:', error);
    } finally {
      setIsApprovingSaving(false);
    }
  };

  // Get row tint class based on approval status and edit status
  const getRowTintClass = () => {
    if (approvalStatus === 'approved') return 'row-approved';
    if (approvalStatus === 'uncertain') return 'row-uncertain';
    // Show yellow tint if needs_edit OR if manually edited (use local state for immediate feedback)
    if (approvalStatus === 'needs_edit' || isLocallyEdited || item.is_manually_edited) return 'row-needs-edit';
    return '';
  };

  // Format approver info for display - uses local state for immediate updates
  const getApproverInfo = (): string | null => {
    if (!approvalStatus) return null;
    const name = localApproverName || currentUserName || currentUserEmail || 'Unknown';
    const date = localApprovedAt ? new Date(localApprovedAt).toLocaleDateString() : 'just now';
    const action = approvalStatus === 'approved' ? 'Approved' : 'Marked for edit';
    return `${action} by ${name} on ${date}`;
  };

  // Get short approver/editor display name - prioritize local state for immediate feedback
  const getApproverDisplayName = (): string | null => {
    // For immediately showing the current user when they approve/reject
    if (approvalStatus && (localApproverName || currentUserName || currentUserEmail)) {
      return localApproverName || currentUserName || currentUserEmail || null;
    }
    return null;
  };

  return (
    <div className={`item-row ${item.status} ${isExpanded ? 'expanded' : ''} ${isPreviewExpanded ? 'preview-expanded' : ''} ${isNewQuestionType ? 'new-question' : ''} ${getRowTintClass()}`}>
      {/* Header row - matching sync version */}
      <div className="item-header" onClick={handleRowClick}>
        <div className="header-left">
          <span className="item-number">#{item.row_number}</span>
          {item.confidence !== undefined && (
            <span
              className="confidence-badge"
              style={{ backgroundColor: getConfidenceColor() }}
            >
              {Math.round(item.confidence * 100)}%
            </span>
          )}
          {isNewQuestionType && (
            <span className="new-question-badge">New Q</span>
          )}
          {item.is_manually_edited && (
            <span
              className="edited-badge"
              title={item.last_edited_by ? `Last edited by ${item.last_edited_by_name || item.last_edited_by}` : undefined}
            >
              ✏️ {item.last_edited_by_name || item.last_edited_by || 'Edited'}
            </span>
          )}
        </div>
        <div className="header-right">
          {isSaving && <span className="saving-indicator">💾</span>}
          {hasChanges && !isSaving && <span className="unsaved-indicator">●</span>}
          {getApproverDisplayName() && (
            <span
              className={`header-approver ${approvalStatus === 'approved' ? 'approved' : 'needs-edit'}`}
              title={getApproverInfo() || ''}
            >
              {approvalStatus === 'approved' ? '✓' : '⚠'} {getApproverDisplayName()}
            </span>
          )}
          <button
            className={`copy-btn ${copied ? 'copied' : ''}`}
            onClick={(e) => { e.stopPropagation(); handleCopyResponse(); }}
            title="Copy complete response"
          >
            {copied ? '✓ Copied!' : '📋 Copy'}
          </button>
          <span className="expand-icon">{isExpanded ? '▼' : isPreviewExpanded ? '◆' : '▶'}</span>
        </div>
      </div>

      {/* Collapsed content preview - truncated Q, A, and sources */}
      {expansionState === 'collapsed' && (
        <div className="collapsed-preview">
          <div className="preview-qa-section" onClick={handleRowClick}>
            <div className="preview-question">
              <strong>Q:</strong> {getQuestionPreview()}
            </div>
            <div className="preview-response">
              <strong>A:</strong> {getResponsePreview() || <em>No response yet</em>}
            </div>
            {getSourcesPreview() && (
              <div className="preview-sources">
                <strong>📎</strong> {getSourcesPreview()}
              </div>
            )}
          </div>
          {/* Context-aware approval/status indicators */}
          <div className="approval-buttons">
            {/* Saved to Q&A: Show "Saved" badge first */}
            {(isAddedToQA || item.is_saved_to_qa) && (
              <div
                className="status-badge saved-badge"
                title={item.saved_to_qa_by_name ? `Saved to Q&A by ${item.saved_to_qa_by_name}` : "Saved to Q&A Bank"}
              >
                <span className="badge-icon">💾</span>
                <span className="badge-text">Saved</span>
              </div>
            )}

            {/* Approved: Show "Approved" badge (highest priority) */}
            {approvalStatus === 'approved' && (
              <div className="status-badge approved-badge" title={getApproverInfo() || "Approved"}>
                <span className="badge-icon">✓</span>
                <span className="badge-text">Approved</span>
                {(localApproverName || currentUserName || currentUserEmail) && (
                  <span className="badge-user">by {localApproverName || currentUserName || currentUserEmail}</span>
                )}
              </div>
            )}

            {/* Uncertain: Show "Not Sure" badge */}
            {approvalStatus === 'uncertain' && (
              <div className="status-badge uncertain-badge" title={`Marked as uncertain by ${localApproverName || 'user'}`}>
                <span className="badge-icon">?</span>
                <span className="badge-text">Not Sure</span>
                {(localApproverName || currentUserName || currentUserEmail) && (
                  <span className="badge-user">by {localApproverName || currentUserName || currentUserEmail}</span>
                )}
              </div>
            )}

            {/* Edited: Show "Edited" badge when manually edited OR marked needs_edit (but not approved/uncertain) */}
            {approvalStatus !== 'approved' && approvalStatus !== 'uncertain' && (isLocallyEdited || item.is_manually_edited || approvalStatus === 'needs_edit') && (
              <div
                className="status-badge edited-badge"
                title={localEditorName ? `Edited by ${localEditorName}` : "Edited"}
              >
                <span className="badge-icon">✎</span>
                <span className="badge-text">Edited</span>
                {(localEditorName || item.last_edited_by_name || item.last_edited_by) && (
                  <span className="badge-user">by {localEditorName || item.last_edited_by_name || item.last_edited_by}</span>
                )}
              </div>
            )}

            {/* Not reviewed and not edited: Show approve/reject/uncertain buttons */}
            {approvalStatus === null && !isLocallyEdited && !item.is_manually_edited && (
              <>
                <button
                  className="approve-btn"
                  onClick={handleApprove}
                  disabled={isApprovingSaving}
                  title="Approve this response"
                >
                  {isApprovingSaving ? '...' : '✓'}
                </button>
                <button
                  className="uncertain-btn"
                  onClick={handleUncertain}
                  disabled={isApprovingSaving}
                  title="Not sure - needs further review"
                >
                  {isApprovingSaving ? '...' : '?'}
                </button>
                <button
                  className="reject-btn"
                  onClick={handleReject}
                  disabled={isApprovingSaving}
                  title="Needs editing"
                >
                  {isApprovingSaving ? '...' : '✕'}
                </button>
              </>
            )}
          </div>
        </div>
      )}

      {/* Intermediate Preview - Full Q, A, and sources (read-only) - click to edit */}
      {isPreviewExpanded && (
        <div className="preview-content" onClick={handleEnterEditMode}>
          <div className="preview-hint">Click anywhere to edit</div>

          {/* Main content wrapper with approval buttons on the right */}
          <div className="preview-main-wrapper">
            <div className="preview-sections">
              <div className="preview-section">
                <div className="preview-label">Question</div>
                <div className="preview-text">{item.original_query}</div>
              </div>
              <div className="preview-section">
                <div className="preview-label">Response</div>
                <div className="preview-text">{response || <em className="no-content">No response yet</em>}</div>
              </div>
              {item.evidence && item.evidence.length > 0 && (
                <div className="preview-section">
                  <div className="preview-label">Sources ({item.evidence.length})</div>
                  <ul className="preview-sources-list" onClick={(e) => e.stopPropagation()}>
                    {item.evidence.map((url, idx) => (
                      <li key={idx}>
                        <a href={url} target="_blank" rel="noopener noreferrer">
                          {getSourceDisplayName(url)}
                        </a>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Context-aware approval buttons - vertically centered on the right */}
            <div className="preview-approval-buttons" onClick={(e) => e.stopPropagation()}>
              {/* Not reviewed and not edited: Show approve/reject/uncertain buttons */}
              {approvalStatus === null && !isLocallyEdited && !item.is_manually_edited && (
                <>
                  <button
                    className="approve-btn-large"
                    onClick={handleApprove}
                    disabled={isApprovingSaving}
                    title="Approve this response"
                  >
                    {isApprovingSaving ? '...' : '✓'}
                  </button>
                  <button
                    className="uncertain-btn-large"
                    onClick={handleUncertain}
                    disabled={isApprovingSaving}
                    title="Not sure - needs further review"
                  >
                    {isApprovingSaving ? '...' : '?'}
                  </button>
                  <button
                    className="reject-btn-large"
                    onClick={handleReject}
                    disabled={isApprovingSaving}
                    title="Needs editing"
                  >
                    {isApprovingSaving ? '...' : '✕'}
                  </button>
                </>
              )}

              {/* Uncertain: Show buttons to approve or reject after further review */}
              {approvalStatus === 'uncertain' && (
                <>
                  <button
                    className="approve-btn-large"
                    onClick={handleApprove}
                    disabled={isApprovingSaving}
                    title="Approve this response"
                  >
                    {isApprovingSaving ? '...' : '✓'}
                  </button>
                  <button
                    className="reject-btn-large"
                    onClick={handleReject}
                    disabled={isApprovingSaving}
                    title="Needs editing"
                  >
                    {isApprovingSaving ? '...' : '✕'}
                  </button>
                </>
              )}

              {/* Edited (manually edited but not rejected via button): Show "Done Editing" button */}
              {approvalStatus === null && (isLocallyEdited || item.is_manually_edited) && (
                <button
                  className="done-editing-btn"
                  onClick={handleDoneEditing}
                  disabled={isApprovingSaving}
                  title="Mark as approved after editing"
                >
                  {isApprovingSaving ? '...' : '✓ Done'}
                </button>
              )}

              {/* Needs edit (rejected via button): Show "Done Editing" button */}
              {approvalStatus === 'needs_edit' && (
                <button
                  className="done-editing-btn"
                  onClick={handleDoneEditing}
                  disabled={isApprovingSaving}
                  title="Mark as complete after editing"
                >
                  {isApprovingSaving ? '...' : '✓ Done'}
                </button>
              )}

              {/* Approved: Show status indicator (already approved) */}
              {approvalStatus === 'approved' && (
                <div className="approved-indicator">
                  <span className="approved-check">✓</span>
                  <span className="approved-label">Approved</span>
                </div>
              )}
            </div>
          </div>

          <div className="preview-actions" onClick={(e) => e.stopPropagation()}>
            <button className="edit-btn" onClick={handleEnterEditMode}>
              ✏️ Edit
            </button>
            <button className="collapse-btn" onClick={() => setExpansionState('collapsed')}>
              ▲ Collapse
            </button>
          </div>
        </div>
      )}

      {/* Regenerate Modal */}
      {showRegenerateModal && (
        <div className="modal-overlay" onClick={handleCancelRegenerate}>
          <div className="modal-content regenerate-modal" onClick={e => e.stopPropagation()}>
            <h3>🔄 Regenerate Response</h3>
            <p>Provide additional context to improve the response:</p>
            <div className="modal-question-preview">
              <strong>Question:</strong> {item.original_query}
            </div>
            <textarea
              value={additionalContext}
              onChange={(e) => setAdditionalContext(e.target.value)}
              placeholder="Enter additional context, clarifications, or specific requirements..."
              className="regenerate-context-input"
              rows={4}
              autoFocus
            />
            <p className="modal-hint">
              This context will be combined with the question to generate a better response.
            </p>
            <div className="modal-actions">
              <button onClick={handleCancelRegenerate} className="modal-cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleConfirmRegenerate}
                className="modal-confirm-btn"
                disabled={!additionalContext.trim() || isRegenerating}
              >
                {isRegenerating ? '⏳ Regenerating...' : '🔄 Regenerate'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add to Q&A Modal */}
      {showAddToQAModal && (
        <div className="modal-overlay" onClick={handleCancelAddToQA}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <h3>➕ Add to Q&A Database</h3>
            <p>Enter the customer/client name for this Q&A entry:</p>
            <div className="modal-question-preview">
              <strong>Question:</strong> {item.original_query}
            </div>
            <input
              type="text"
              value={customerNameInput}
              onChange={(e) => setCustomerNameInput(e.target.value)}
              placeholder="e.g., ABC Corporation"
              className="customer-name-input"
              autoFocus
            />
            <p className="modal-hint">
              This is the company that sent you this questionnaire.
            </p>
            <div className="modal-actions">
              <button onClick={handleCancelAddToQA} className="modal-cancel-btn">
                Cancel
              </button>
              <button
                onClick={handleConfirmAddToQA}
                className="modal-confirm-btn"
                disabled={!customerNameInput.trim() || isAddingToQA}
              >
                {isAddingToQA ? '⏳ Adding...' : '➕ Add to Q&A'}
              </button>
            </div>
          </div>
        </div>
      )}

      {isExpanded && (
        <div className="item-content">
          {/* Close/Back button for edit mode */}
          <div className="edit-mode-header">
            <button className="back-to-preview-btn" onClick={handleCloseEditMode}>
              ← Back
            </button>
            <span className="edit-mode-title">#{item.row_number}: {item.original_query}</span>
          </div>

          {/* Response section with regenerate button */}
          <div className="field-group">
            <div className="response-header">
              <label>Response</label>
              <button
                className="regenerate-inline-btn"
                onClick={handleOpenRegenerateModal}
                disabled={item.status === 'processing'}
                title="Regenerate response with additional context"
              >
                🔄 Regenerate
              </button>
            </div>
            <textarea
              value={response}
              onChange={e => handleResponseChange(e.target.value)}
              placeholder={item.status === 'pending' ? 'Waiting to process...' : 'No response yet'}
              disabled={item.status === 'processing'}
              rows={4}
            />
          </div>

          {/* Sources/Evidence section with add/remove/refresh */}
          <div className="field-group">
            <div className="sources-header">
              <label>Sources</label>
              <button
                className={`refresh-sources-btn ${isRefreshingSources ? 'refreshing' : ''}`}
                onClick={handleRefreshSources}
                disabled={isRefreshingSources || !item.response}
                title="Re-analyze response to find referenced documents"
              >
                {isRefreshingSources ? '⏳ Refreshing...' : '🔄 Refresh'}
              </button>
            </div>

            {/* Sources list with remove buttons */}
            {item.evidence && item.evidence.length > 0 ? (
              <ul className="sources-list">
                {item.evidence.map((url, idx) => (
                  <li key={idx} className="source-item">
                    <button
                      className="remove-source-btn"
                      onClick={() => handleRemoveSource(url)}
                      title="Remove this source"
                    >
                      ✕
                    </button>
                    <a href={url} target="_blank" rel="noopener noreferrer">
                      {getSourceDisplayName(url)}
                    </a>
                  </li>
                ))}
              </ul>
            ) : (
              <p className="no-sources">No sources yet. Click Refresh to find matching documents.</p>
            )}

            {/* Add Source Button */}
            {!showAddSourcePanel && (
              <button
                className="add-source-btn"
                onClick={handleOpenAddSource}
                title="Add a source document"
              >
                <span className="add-icon">+</span>
                <span>Add Source</span>
              </button>
            )}

            {/* Add Source Search Panel */}
            {showAddSourcePanel && (
              <div className="add-source-panel">
                <div className="add-source-header">
                  <span>Search & Add Sources</span>
                  <button
                    className="close-panel-btn"
                    onClick={() => setShowAddSourcePanel(false)}
                  >
                    ✕
                  </button>
                </div>
                <input
                  type="text"
                  className="source-search-input"
                  placeholder="Search documents..."
                  value={sourceSearchTerm}
                  onChange={(e) => setSourceSearchTerm(e.target.value)}
                  autoFocus
                />
                <ul className="source-search-results">
                  {getFilteredSources().slice(0, 10).map((source, idx) => (
                    <li
                      key={idx}
                      className="source-search-item"
                      onClick={() => handleAddSource(source)}
                    >
                      <span className="source-name">{source.filename}</span>
                      {source.public_url && (
                        <span className="source-url-preview">
                          {source.public_url.substring(0, 50)}...
                        </span>
                      )}
                    </li>
                  ))}
                  {getFilteredSources().length === 0 && sourceSearchTerm && (
                    <li className="source-no-results">No matching documents found</li>
                  )}
                  {getFilteredSources().length === 0 && !sourceSearchTerm && urlMappings.length > 0 && (
                    <li className="source-hint">Type to search {urlMappings.length} available documents</li>
                  )}
                </ul>
              </div>
            )}
          </div>

          {/* Custom Fields - editable */}
          {customFields && customFields.length > 0 && (
            <div className="custom-fields-section">
              <div className="section-header">
                <label>Custom Fields</label>
              </div>
              <div className="custom-fields-grid">
                {customFields.map((field) => (
                  <div key={field.name} className="custom-field-item">
                    <label>{field.name}</label>
                    <input
                      type="text"
                      value={customFieldValues[field.name] || ''}
                      onChange={(e) => handleCustomFieldChange(field.name, e.target.value)}
                      placeholder={field.description || `Enter ${field.name}`}
                      disabled={item.status === 'processing'}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Error message */}
          {item.error_message && (
            <div className="error-message">{item.error_message}</div>
          )}

          {/* Action buttons - matching sync version style */}
          <div className="row-action-buttons">
            {onAddToQA && !isAddedToQA && (
              <button
                className="add-single-qa-button"
                onClick={handleOpenAddToQAModal}
                title="Add this Q&A pair to knowledge base"
              >
                ➕ Add to Q&A
              </button>
            )}
            {isAddedToQA && (
              <span className="added-to-qa-badge">✓ Added to Q&A</span>
            )}
            {item.debug_info && (
              <button
                className={`debug-toggle-button ${showDebug ? 'expanded' : ''}`}
                onClick={() => setShowDebug(!showDebug)}
              >
                🔍 {showDebug ? 'Hide' : 'Show'} Debug Info
              </button>
            )}
          </div>

          {/* Debug Panel */}
          {showDebug && item.debug_info && renderDebugPanel(item.debug_info)}
        </div>
      )}
    </div>
  );
};

export default QuestionnaireItemRow;

